import type {HVBroadcastActions} from 'src/action-creators/hv-broadcasts';

import type {
  KeyedHVBroadcastSuppression,
  fileCsvUploadEntries,
  ContactList,
  Filters,
  FileDetails,
  HVBroadcastSuppressionTimeFrame,
  HVBroadcastDataWithPagination,
  ScheduledOrNewHVBroadcast,
  PerformanceDetailsResponse,
  RenameModalPopupDetails,
  SegmentPerformanceDetailsResponse,
} from 'src/types/hv-broadcasts';
import {
  RECEIVE_ALL_HV_BROADCASTS,
  RECEIVE_HV_BROADCAST_SUPPRESSION,
  RESTORE_HV_BROADCAST_PHONE_NUMBERS,
  SET_CSV_FILE_UPLOAD_HEADERS,
  SET_CSV_FILE_UPLOAD_ENTRIES,
  SET_CSV_FILE_UPLOAD,
  SET_PERFORMANCE_DATA,
  SET_MESSAGE_DATA,
  RECEIVE_HV_BROADCAST_FILTERS,
  RECEIVE_HV_CONTACT_LISTS,
  RECEIVE_AGENTS_RECENT_HV_CONTACT_LISTS,
  RECEIVE_HV_BROADCAST_SUPPRESSION_TIME_FRAME,
  RECEIVE_UPLOADED_FILE_DETAILS,
  REMOVE_UPLOADED_FILE_DETAILS,
  UPDATE_CONTACT_LIST_LIMIT_REACHED,
  UPDATE_CONTACT_LIST_ID_CREATED,
  CLEAR_UPLOADED_FILE_DETAILS,
  INITIALIZE_UPLOADED_FILE_DETAILS,
  RECEIVE_PERFORMANCE_DELIVERY_ANALYTICS,
  RECEIVE_HV_BROADCAST_UPDATE_NAME,
  RECEIVE_CURRENT_HV_BROADCAST_DATA,
  REMOVE_DELETED_HV_BROADCAST,
  SET_SCHEDULER_TEMPLATE_ID,
  SET_SCHEDULER_TEMPLATE_BODY,
  SET_SHOW_RENAME_MODAL_POPUP_DETAILS,
  UPDATE_PERFORMANCE_DATA_NAME,
  CLEAR_CURRENT_BROADCAST_DATA,
  CLEAR_HV_BROADCASTS,
  SET_SELECTED_BROADCASTS_TAB,
  CLEAR_HV_BROADCAST_RESTORED_PHONE_NUMBERS,
  CLEAR_HV_BROADCAST_SUPPRESSION,
  SET_BROADCAST_DETAILS,
  RECEIVE_BROADCAST_PERFORMANCE_RECIPIENTS,
  SET_SELECTED_CHANNEL,
  SET_SUPPRESSION_API_COMPLETE,
  SET_CURRENTLY_APPLIED_HVB_SUPPRESSION,
  SET_BROADCAST_DRIP_STEP_NUMBER,
  SET_SEGMENT_PERFORMANCE_DATA,
} from 'src/action-creators/hv-broadcasts';

import keyBy from 'lodash/keyBy';


type HVBroadcastState = {
  hvBroadcasts: HVBroadcastDataWithPagination;
  hvBroadcastSuppression: KeyedHVBroadcastSuppression;
  hvBroadcastRestoredPhoneNumbers: string[];
  fileCsvUploadHeaders: string[];
  fileCsvUploadEntries: fileCsvUploadEntries[];
  fileCsvUpload: File | null;
  filters: Filters;
  contactLists: ContactList[];
  agentsRecentContactLists: ContactList[];
  broadcastSuppressionTimeFrame: HVBroadcastSuppressionTimeFrame;
  uploadedFileDetails: FileDetails[];
  contactListLimitReached: boolean;
  listCreatedId: string;
  hvBroadcastMessage: ScheduledOrNewHVBroadcast;
  hvBroadcastPerformance: PerformanceDetailsResponse;
  segmentPerformanceData: SegmentPerformanceDetailsResponse;
  currentBroadcastData: ScheduledOrNewHVBroadcast;
  templateId: string;
  schedulerTemplateId: string;
  schedulerTemplateBody: string;
  showRenameModalPopupDetails: RenameModalPopupDetails;
  selectedBroadcastType: string;
  broadcastDetails: ScheduledOrNewHVBroadcast;
  selectedChannel: string;
  placeholders: null | {
    [key: string]: string;
  };
  suppressionApiComplete: boolean;
  broadcastDripStepNumber: number;
};

export const initialDraftSegment = {
  id: '',
  body: '',
  schedulerTemplateId: '',
  sendDate: '',
  status: '',
  templateId: null,
  sendNow: true,
  specificDay: false,
  specificTime: '',
  placeholders: null,
  segmentError: false,
  specificDaysAfterInitialMessage: 0,
};

export const draftNewHVBroadcastDetails: ScheduledOrNewHVBroadcast = {
  name: '',
  senderName: '',
  phoneNumberSetName: '',
  phoneNumberSetId: '',
  channel: 'sms',
  sentTime: '',
  messageBody: '',
  totalRecipients: 0,
  contactLists: [],
  files: [],
  grabbedNumbers: null,
  sendNow: true,
  id: '',
  autoReplies: [],
  segments: [{...initialDraftSegment}],
};

const initialDraftMessage = {
  name: '',
  senderName: '',
  phoneNumberSetName: '',
  phoneNumberSetId: '',
  channel: 'sms',
  sentTime: '',
  messageBody: '',
  totalRecipients: 0,
  contactLists: [],
  files: [],
  grabbedNumbers: null,
  grabbedPhoneNumbers: [],
  sendNow: true,
  id: '',
  schedulerTemplateId: '',
  schedulerTemplateBody: '',
  placeholders: null,
  applySuppression: true,
  autoReplies: [],
  segments: [initialDraftSegment],
  segmentLimitExceededError: false,
};

const initialHVBroadcasts = {
  broadcasts: {},
  page: 0,
  recordsPerPage: 0,
  totalPages: 0,
  totalRecords: 0,
};

const initialTimeFrame = {
  broadcastSuppresionTimeType: 'DAY_3',
  time: {
    number: 3,
    unit: 'day',
  },
};

const initialState: HVBroadcastState = {
  hvBroadcasts: initialHVBroadcasts,
  hvBroadcastSuppression: {},
  hvBroadcastRestoredPhoneNumbers: [],
  segmentPerformanceData: {
    sensePhone: '',
    totalRecipients: 0,
    recipientDetails: [],
    page: 0,
    pageSize: 0,
    totalPages: 0,
    segmentId: '',
    broadcastId: '',
  },
  hvBroadcastPerformance: {
    deliveryAnalytics: {
      delivered: {
        total: 0,
        percent: 0,
      },
      responded: {
        total: 0,
        percent: 0,
      },
      failed: {
        total: 0,
        percent: 0,
      },
      optedOut: {
        total: 0,
        percent: 0,
      },
      skipped: {
        total: 0,
        percent: 0,
      },
      sent: {
        total: 0,
        percent: 0,
      },
    },
    name: '',
    page: 0,
    recipients: [],
    recordsPerPage: 0,
    sensePhone: '',
    totalPages: 0,
    totalRecipients: 0,
    totalRecords: 0,
    id: '',
    autoReplies: [],
    segments: [],
    status: '',
  },
  hvBroadcastMessage: initialDraftMessage,
  fileCsvUploadHeaders: [],
  fileCsvUploadEntries: [],
  fileCsvUpload: null,
  filters: {phoneNumberSets: [], sender: []},
  contactLists: [],
  agentsRecentContactLists: [],
  broadcastSuppressionTimeFrame: initialTimeFrame,
  uploadedFileDetails: [],
  contactListLimitReached: false,
  listCreatedId: '',
  currentBroadcastData: initialDraftMessage,
  templateId: '',
  schedulerTemplateId: '',
  schedulerTemplateBody: '',
  showRenameModalPopupDetails: {id: '', name: '', nameBroadcast: false},
  selectedBroadcastType: 'sent',
  broadcastDetails: initialDraftMessage,
  selectedChannel: 'sms',
  placeholders: null,
  suppressionApiComplete: false,
  broadcastDripStepNumber: 0,
};

export default (
  state: HVBroadcastState = initialState,
  action: HVBroadcastActions,
): HVBroadcastState => {
  switch (action.type) {
    case RECEIVE_ALL_HV_BROADCASTS:
      const update = action.payload.update;
      const keyedBroadcasts = keyBy(action.payload.data.broadcasts, 'id');
      const broadcastDetails = {
        ...state.hvBroadcasts.broadcasts,
        ...keyedBroadcasts,
      } as const;
      return {
        ...state,
        hvBroadcasts: update
          ? {
              ...action.payload.data,
              broadcasts: broadcastDetails,
            }
          : {...action.payload.data, broadcasts: keyedBroadcasts},
      };
    case RECEIVE_HV_BROADCAST_UPDATE_NAME:
      const {id, name} = action.payload;
      const currentBroadcast = state.hvBroadcasts.broadcasts[id];
      currentBroadcast.name = name;
      const allBroadcasts = {
        ...state.hvBroadcasts.broadcasts,
        [id]: currentBroadcast,
      } as const;
      return {
        ...state,
        hvBroadcasts: {...state.hvBroadcasts, broadcasts: allBroadcasts},
      };
    case RECEIVE_CURRENT_HV_BROADCAST_DATA:
      const {templateId = '', schedulerTemplateId = ''} = action.payload;
      return {
        ...state,
        currentBroadcastData: action.payload,
        broadcastDetails: action.payload,
        templateId,
        schedulerTemplateId,
        broadcastDripStepNumber: 0,
      };
    case REMOVE_DELETED_HV_BROADCAST:
      const {[action.payload]: removed, ...filteredBroadcasts} =
        state.hvBroadcasts.broadcasts;
      return {
        ...state,
        hvBroadcasts: {
          ...state.hvBroadcasts,
          broadcasts: filteredBroadcasts,
        },
      };
    case RECEIVE_HV_BROADCAST_SUPPRESSION:
      const filteredSuppression = action.payload.filter(
        (suppression) =>
          suppression.type === 'invalid' ||
          suppression.type === 'landline' ||
          suppression.optedOut ||
          suppression.activePlacement,
      );
      return {
        ...state,
        hvBroadcastSuppression: keyBy(filteredSuppression, 'phoneNumber'),
      };
    case RECEIVE_HV_BROADCAST_SUPPRESSION_TIME_FRAME:
      return {
        ...state,
        broadcastSuppressionTimeFrame: action.payload,
      };
    case RESTORE_HV_BROADCAST_PHONE_NUMBERS:
      return {
        ...state,
        hvBroadcastRestoredPhoneNumbers: [
          ...state.hvBroadcastRestoredPhoneNumbers,
          ...action.payload,
        ],
      };
    case CLEAR_HV_BROADCAST_RESTORED_PHONE_NUMBERS:
      return {
        ...state,
        hvBroadcastRestoredPhoneNumbers: [],
        hvBroadcastSuppression: {},
        broadcastSuppressionTimeFrame: initialTimeFrame,
      };
    case CLEAR_HV_BROADCAST_SUPPRESSION: {
      return {
        ...state,
        hvBroadcastSuppression: {},
      };
    }
    case SET_CSV_FILE_UPLOAD_HEADERS:
      return {
        ...state,
        fileCsvUploadHeaders: action.payload,
      };
    case SET_CSV_FILE_UPLOAD_ENTRIES:
      return {
        ...state,
        fileCsvUploadEntries: action.payload,
      };
    case SET_CSV_FILE_UPLOAD:
      return {
        ...state,
        fileCsvUpload: action.payload,
      };
    case RECEIVE_HV_BROADCAST_FILTERS:
      return {
        ...state,
        filters: action.payload,
      };
    case SET_PERFORMANCE_DATA:
      return {...state, hvBroadcastPerformance: action.payload};
    case UPDATE_PERFORMANCE_DATA_NAME:
      if (action.payload.id === state.hvBroadcastPerformance.id) {
        return {
          ...state,
          hvBroadcastPerformance: {
            ...state.hvBroadcastPerformance,
            name: action.payload.name,
          },
        };
      } else {
        return state;
      }
    case SET_SEGMENT_PERFORMANCE_DATA:
      return {...state, segmentPerformanceData: action.payload};
    case RECEIVE_PERFORMANCE_DELIVERY_ANALYTICS:
      if (state.hvBroadcastPerformance.id === action.payload.id) {
        return {
          ...state,
          hvBroadcastPerformance: {
            ...state.hvBroadcastPerformance,
            deliveryAnalytics: action.payload.deliveryAnalytics,
          },
        };
      } else {
        return state;
      }
    case RECEIVE_BROADCAST_PERFORMANCE_RECIPIENTS:
      if (state.hvBroadcastPerformance.id === action.payload.id) {
        return {
          ...state,
          hvBroadcastPerformance: {
            ...state.hvBroadcastPerformance,
            ...action.payload,
          },
        };
      } else {
        return state;
      }
    case SET_MESSAGE_DATA:
      return {...state, hvBroadcastMessage: action.payload};
    case RECEIVE_HV_CONTACT_LISTS:
      const updateList = action.payload.update;
      const receivedContactList = action.payload.contactLists;
      return {
        ...state,
        contactLists: updateList
          ? [...state.contactLists, ...receivedContactList]
          : receivedContactList,
      };
    case RECEIVE_AGENTS_RECENT_HV_CONTACT_LISTS:
      return {
        ...state,
        agentsRecentContactLists: action.payload,
      };
    case RECEIVE_UPLOADED_FILE_DETAILS:
      return {
        ...state,
        uploadedFileDetails: [...state.uploadedFileDetails, action.payload],
      };
    case REMOVE_UPLOADED_FILE_DETAILS:
      const filteredResults = state.uploadedFileDetails.filter(
        (file) => file.id !== action.payload,
      );
      return {
        ...state,
        uploadedFileDetails: filteredResults,
      };
    case UPDATE_CONTACT_LIST_LIMIT_REACHED:
      return {
        ...state,
        contactListLimitReached: action.payload,
      };
    case UPDATE_CONTACT_LIST_ID_CREATED:
      return {
        ...state,
        listCreatedId: action.payload,
      };
    case CLEAR_UPLOADED_FILE_DETAILS:
      return {
        ...state,
        uploadedFileDetails: [],
      };
    case INITIALIZE_UPLOADED_FILE_DETAILS:
      return {
        ...state,
        uploadedFileDetails: action.payload,
      };
    case SET_SCHEDULER_TEMPLATE_ID:
      return {...state, schedulerTemplateId: action.payload};
    case SET_SCHEDULER_TEMPLATE_BODY:
      return {...state, schedulerTemplateBody: action.payload};
    case SET_SHOW_RENAME_MODAL_POPUP_DETAILS:
      return {...state, showRenameModalPopupDetails: action.payload};
    case CLEAR_CURRENT_BROADCAST_DATA:
      return {...state, currentBroadcastData: initialDraftMessage};
    case CLEAR_HV_BROADCASTS:
      return {...state, hvBroadcasts: initialHVBroadcasts};
    case SET_SELECTED_BROADCASTS_TAB:
      return {...state, selectedBroadcastType: action.payload};
    case SET_BROADCAST_DETAILS:
      return {...state, broadcastDetails: action.payload};
    case SET_SELECTED_CHANNEL:
      return {...state, selectedChannel: action.payload};
    case SET_SUPPRESSION_API_COMPLETE:
      return {
        ...state,
        suppressionApiComplete: action.payload,
      };
    case SET_CURRENTLY_APPLIED_HVB_SUPPRESSION:
      return {
        ...state,
        broadcastDetails: {
          ...state.broadcastDetails,
          applySuppression: action.payload.value,
        },
      };
    case SET_BROADCAST_DRIP_STEP_NUMBER:
      return {
        ...state,
        broadcastDripStepNumber: action.payload,
      };
    default:
      return state;
  }
};
